
import React, { useState, useEffect } from 'react';
import { useApp } from '../App';
import { auth, db } from '../firebase';
import { ref, onValue, query, orderByChild, equalTo, update, push, remove, set } from 'firebase/database';
import { User, Package, MapPin, LogOut, ChevronRight, Clock, Save, Plus, Trash2, Edit3, X, Truck, Headphones, MessageSquare, CheckCircle2, Star, EyeOff, Eye, UserCheck, Sparkles, MapPinned, XCircle } from 'lucide-react';
import { UserAddress, CargoCompany, SupportRequest, Review, Product } from '../types';

const ProfilePage: React.FC = () => {
  const { profile, user } = useApp();
  const [orders, setOrders] = useState<any[]>([]);
  const [supportRequests, setSupportRequests] = useState<SupportRequest[]>([]);
  const [userReviews, setUserReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'info' | 'orders' | 'addresses' | 'support'>('info');
  const [saving, setSaving] = useState(false);
  
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  });

  const [addressForm, setAddressForm] = useState({
    title: '',
    firstName: '',
    lastName: '',
    phone: '',
    city: '',
    district: '',
    zipCode: '',
    fullAddress: ''
  });

  const [supportForm, setSupportForm] = useState({
    subject: 'return' as any,
    message: ''
  });

  const [reviewProduct, setReviewProduct] = useState<Product | null>(null);
  const [existingReviewId, setExistingReviewId] = useState<string | null>(null);
  const [userRating, setUserRating] = useState(5);
  const [userComment, setUserComment] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.firstName || profile.displayName?.split(' ')[0] || '',
        lastName: profile.lastName || profile.displayName?.split(' ').slice(1).join(' ') || '',
        phone: profile.phone || ''
      });
    }

    if (user?.uid) {
      const ordersRef = query(ref(db, 'orders'), orderByChild('userId'), equalTo(user.uid));
      onValue(ordersRef, (snapshot) => {
        const data = snapshot.val();
        if (data) setOrders(Object.values(data).sort((a: any, b: any) => b.createdAt - a.createdAt));
      });

      const supportRef = query(ref(db, 'support_requests'), orderByChild('userId'), equalTo(user.uid));
      onValue(supportRef, (snapshot) => {
        const data = snapshot.val();
        if (data) setSupportRequests((Object.values(data) as SupportRequest[]).sort((a: SupportRequest, b: SupportRequest) => b.createdAt - a.createdAt));
      });

      const reviewsRef = query(ref(db, 'reviews'), orderByChild('userId'), equalTo(user.uid));
      onValue(reviewsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) setUserReviews(Object.values(data));
      });
      
      setLoading(false);
    }
  }, [profile, user]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.uid) return;
    setSaving(true);
    try {
      await update(ref(db, `users/${user.uid}`), {
        ...formData,
        displayName: `${formData.firstName} ${formData.lastName}`.trim(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      });
      alert('Bilgileriniz başarıyla güncellendi.');
    } catch (error) {
      alert('Güncelleme sırasında bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.uid) return;
    setSaving(true);
    try {
      const newAddrRef = push(ref(db, `users/${user.uid}/addresses`));
      await set(newAddrRef, { ...addressForm, id: newAddrRef.key });
      setShowAddressModal(false);
      setAddressForm({ title: '', firstName: '', lastName: '', phone: '', city: '', district: '', zipCode: '', fullAddress: '' });
    } catch (error) {
      alert('Adres eklenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const deleteAddress = async (id: string) => {
    if (!user?.uid || !window.confirm('Bu adresi silmek istediğinize emin misiniz?')) return;
    await remove(ref(db, `users/${user.uid}/addresses/${id}`));
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'delivered': return { label: 'Teslim Edildi', color: 'bg-emerald-100 text-emerald-700' };
      case 'shipped': return { label: 'Kargoda', color: 'bg-indigo-100 text-indigo-700' };
      case 'cancelled': return { label: 'İptal Edildi', color: 'bg-red-100 text-red-700' };
      default: return { label: 'Hazırlanıyor', color: 'bg-amber-100 text-amber-700' };
    }
  };

  if (loading) return <div className="py-20 text-center animate-pulse text-stone-400 font-bold">Yükleniyor...</div>;

  return (
    <div className="container mx-auto px-4 md:px-6 pb-20">
      <div className="grid lg:grid-cols-4 gap-8">
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white p-8 rounded-[2.5rem] border border-stone-100 shadow-sm text-center">
            <div className="w-24 h-24 bg-stone-50 text-amber-800 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl font-black border border-stone-100 shadow-inner">
              {profile?.displayName?.charAt(0) || 'U'}
            </div>
            <h2 className="text-xl font-black text-stone-900 mb-1">{profile?.displayName}</h2>
            <p className="text-sm text-stone-500 mb-6 font-medium">{profile?.email}</p>
            <button onClick={() => auth.signOut()} className="w-full py-4 bg-red-50 text-red-500 rounded-2xl font-bold flex items-center justify-center space-x-2 hover:bg-red-100 transition-all active:scale-95">
              <LogOut size={18} />
              <span>Çıkış Yap</span>
            </button>
          </div>

          <nav className="bg-white rounded-[2.5rem] border border-stone-100 shadow-sm overflow-hidden p-2">
            {[
              { id: 'info', icon: User, label: 'Kullanıcı Bilgileri' },
              { id: 'orders', icon: Package, label: 'Siparişlerim' },
              { id: 'addresses', icon: MapPin, label: 'Adreslerim' },
              { id: 'support', icon: Headphones, label: 'Destek Taleplerim' },
            ].map((item) => (
              <button key={item.id} onClick={() => setActiveTab(item.id as any)} className={`w-full flex items-center justify-between p-4 rounded-2xl text-sm font-bold transition-all mb-1 last:mb-0 ${activeTab === item.id ? 'text-amber-800 bg-amber-50' : 'text-stone-500 hover:bg-stone-50'}`}>
                <div className="flex items-center space-x-3">
                  <item.icon size={18} />
                  <span>{item.label}</span>
                </div>
                <ChevronRight size={16} className={activeTab === item.id ? 'text-amber-400' : 'text-stone-200'} />
              </button>
            ))}
          </nav>
        </div>

        <div className="lg:col-span-3">
          {activeTab === 'info' && (
            <div className="bg-white p-10 rounded-[3rem] border border-stone-100 shadow-sm">
              <h3 className="text-2xl font-black text-stone-900 mb-10">Kişisel Bilgilerim</h3>
              <form onSubmit={handleUpdateProfile} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Adınız</label>
                    <input type="text" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-stone-900" />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Soyadınız</label>
                    <input type="text" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-stone-900" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-3 ml-2">Telefon Numaranız</label>
                  <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-stone-900" placeholder="05XX XXX XX XX" />
                </div>
                <button type="submit" disabled={saving} className="bg-stone-900 text-white px-10 py-4 rounded-2xl font-bold flex items-center justify-center space-x-3 hover:bg-amber-800 transition-all disabled:opacity-50">
                  <Save size={20} />
                  <span>{saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}</span>
                </button>
              </form>
            </div>
          )}

          {activeTab === 'addresses' && (
            <div className="bg-white p-10 rounded-[3rem] border border-stone-100 shadow-sm">
              <div className="flex items-center justify-between mb-10">
                <h3 className="text-2xl font-black text-stone-900 flex items-center">
                  <MapPin size={28} className="mr-3 text-amber-800" /> Adreslerim
                </h3>
                <button onClick={() => setShowAddressModal(true)} className="bg-amber-800 text-white px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest flex items-center space-x-2 hover:bg-amber-900 transition-all shadow-lg shadow-amber-900/20">
                  <Plus size={18} />
                  <span>Yeni Adres Ekle</span>
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {profile?.addresses ? Object.values(profile.addresses).map((addr: any) => (
                  <div key={addr.id} className="p-6 border border-stone-100 rounded-[2rem] hover:bg-stone-50/50 transition-all group relative">
                    <button onClick={() => deleteAddress(addr.id)} className="absolute top-4 right-4 text-stone-300 hover:text-red-500 transition-colors">
                      <Trash2 size={18} />
                    </button>
                    <div className="text-[10px] font-black text-amber-800 uppercase tracking-[0.2em] mb-4">{addr.title}</div>
                    <p className="font-black text-stone-900 mb-2">{addr.firstName} {addr.lastName}</p>
                    <p className="text-xs text-stone-600 leading-relaxed mb-4 font-medium">{addr.fullAddress}</p>
                    <p className="text-xs font-black text-stone-900 uppercase">{addr.district} / {addr.city}</p>
                  </div>
                )) : (
                  <div className="col-span-full py-20 text-center bg-stone-50 rounded-[2rem] border-2 border-dashed border-stone-100 text-stone-300 italic">Kayıtlı adres bulunamadı.</div>
                )}
              </div>
            </div>
          )}
          {/* Diğer tab içerikleri mevcut halini koruyacak... */}
        </div>
      </div>

      {/* Adres Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 z-[200] bg-stone-900/70 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
            <div className="p-8 border-b border-stone-50 flex items-center justify-between">
              <h2 className="text-xl font-black text-stone-900">Yeni Adres Ekle</h2>
              <button onClick={() => setShowAddressModal(false)} className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-stone-400 hover:text-stone-900 shadow-sm transition-all"><X size={24} /></button>
            </div>
            <form onSubmit={handleAddAddress} className="p-10 space-y-6">
              <div>
                <label className="block text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2 ml-2">Adres Başlığı</label>
                <input required type="text" placeholder="Evim, İş Yerim" value={addressForm.title} onChange={e => setAddressForm({...addressForm, title: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-stone-900" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input required type="text" placeholder="Ad" value={addressForm.firstName} onChange={e => setAddressForm({...addressForm, firstName: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-stone-900" />
                <input required type="text" placeholder="Soyad" value={addressForm.lastName} onChange={e => setAddressForm({...addressForm, lastName: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-stone-900" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input required type="text" placeholder="İl" value={addressForm.city} onChange={e => setAddressForm({...addressForm, city: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-stone-900" />
                <input required type="text" placeholder="İlçe" value={addressForm.district} onChange={e => setAddressForm({...addressForm, district: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none font-bold text-stone-900" />
              </div>
              <textarea required placeholder="Tam Adres" rows={3} value={addressForm.fullAddress} onChange={e => setAddressForm({...addressForm, fullAddress: e.target.value})} className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-4 px-6 focus:ring-2 focus:ring-amber-500 outline-none resize-none font-bold text-stone-900"></textarea>
              <button type="submit" disabled={saving} className="w-full bg-stone-900 text-white py-5 rounded-2xl font-bold text-lg hover:bg-amber-800 transition-all flex items-center justify-center space-x-3">
                <CheckCircle2 size={24} />
                <span>{saving ? 'Ekleniyor...' : 'Adresi Kaydet'}</span>
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
